import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, CircularProgress, Alert, Pagination, Button,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import SearchFilters from '../../components/company/SearchFilters';
import ProfileSearchCard from '../../components/company/ProfileSearchCard';
import { companyService, SearchFilters as Filters } from '../../services/companyService';

const emptyFilters = { prefecture: '', workStyle: '', competitionPriority: '', strength: '' };

const ProfileSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(emptyFilters);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const doSearch = async (searchPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const searchFilters: Filters = { page: searchPage };
      if (filters.prefecture) searchFilters.prefecture = filters.prefecture;
      if (filters.workStyle) searchFilters.workStyle = filters.workStyle;
      if (filters.competitionPriority) searchFilters.competitionPriority = filters.competitionPriority;
      if (filters.strength) searchFilters.strength = filters.strength;

      const result = await companyService.searchProfiles(searchFilters);
      setProfiles(result.profiles);
      setTotal(result.total);
      setPage(result.page);
      setTotalPages(result.totalPages);
    } catch (err: any) {
      setError(err.response?.data?.message || '検索に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => doSearch(1);
  const handleClear = () => {
    setFilters(emptyFilters);
    // emptyFiltersで即検索（setFiltersはバッチ更新のためdoSearchに直接渡す）
    setLoading(true);
    setError('');
    companyService.searchProfiles({ page: 1 })
      .then((result) => {
        setProfiles(result.profiles);
        setTotal(result.total);
        setPage(result.page);
        setTotalPages(result.totalPages);
      })
      .catch((err: any) => setError(err.response?.data?.message || '検索に失敗しました'))
      .finally(() => setLoading(false));
  };
  const handlePageChange = (_: any, p: number) => doSearch(p);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="lg">
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/company/dashboard')}
            sx={{ color: 'white', mb: 1 }}>
            ダッシュボード
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            アスリートプロフィール検索
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {total}件のプロフィールが見つかりました
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* フィルタサイドバー */}
          <Grid item xs={12} md={3}>
            <SearchFilters filters={filters} onChange={setFilters} onSearch={handleSearch} onClear={handleClear} />
          </Grid>

          {/* 結果一覧 */}
          <Grid item xs={12} md={9}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : profiles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: '#666' }}>
                  条件に一致するプロフィールが見つかりませんでした
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {profiles.map((profile) => (
                    <Grid item xs={12} sm={6} key={profile.athleteUuid}>
                      <ProfileSearchCard profile={profile} />
                    </Grid>
                  ))}
                </Grid>
                {totalPages > 1 && (
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProfileSearchPage;
